import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { familyApi } from '../services/api';
import FamilyCard from '../components/family/FamilyCard';
import CreateFamilyForm from '../components/family/CreateFamilyForm';
import JoinFamilyForm from '../components/family/JoinFamilyForm';
import LoadingSpinner from '../components/LoadingSpinner';
import ReadersPage from './ReadersPage';
import './FamilyPage.css';

const FamilyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showReaders = location.hash === '#readers';
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);

  useEffect(() => {
    loadFamilies();
  }, []);

  const loadFamilies = async () => {
    try {
      setLoading(true);
      const response = await familyApi.getFamilies();
      setFamilies(response.data.families || []);
      setError(null);
    } catch (err) {
      console.error('Error loading families:', err);
      setError(err.response?.data?.error || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  const handleFamilyCreated = (newFamily) => {
    setFamilies(prev => [...prev, newFamily]);
    setShowCreateForm(false);
  };

  const handleFamilyJoined = () => {
    loadFamilies(); // Reload all families
    setShowJoinForm(false);
  };

  const handleLeaveFamily = async (familyId) => {
    if (!window.confirm('Are you sure you want to leave this family?')) {
      return;
    }

    try {
      await familyApi.leaveFamily(familyId);
      setFamilies(prev => prev.filter(family => family.id !== familyId));
    } catch (err) {
      console.error('Error leaving family:', err);
      setError(err.response?.data?.error || 'Failed to leave family');
    }
  };

  const handleDeleteFamily = async (familyId, familyName) => {
    if (!window.confirm(`Are you sure you want to delete the family "${familyName}"?\n\nThis will permanently delete the family group, but all readers will remain active and can be added to other families.`)) {
      return;
    }

    try {
      await familyApi.deleteFamily(familyId);
      setFamilies(prev => prev.filter(family => family.id !== familyId));
    } catch (err) {
      console.error('Error deleting family:', err);
      setError(err.response?.data?.error || 'Failed to delete family');
    }
  };

  return (
    <div className="family-page">
      <div className="family-header">
        <h2>Family</h2>
        <p>Invite others to join your reading journey.</p>
        <div className="family-tabs" role="tablist" aria-label="Family">
          <button
            type="button"
            role="tab"
            aria-selected={!showReaders}
            className={`family-tab ${!showReaders ? 'family-tab--active' : ''}`}
            onClick={() => navigate('/family')}
          >
            Families
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={showReaders}
            className={`family-tab ${showReaders ? 'family-tab--active' : ''}`}
            onClick={() => navigate('/family#readers')}
          >
            Readers
          </button>
        </div>
      </div>

      {showReaders && <ReadersPage />}

      {!showReaders && loading && (
        <LoadingSpinner size="large" message="Loading families..." />
      )}

      {!showReaders && !loading && (
      <>
        <div className="family-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            Create a family
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowJoinForm(true)}
          >
            Join a family
          </button>
        </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="families-container">
        {families.length === 0 ? (
          <div className="empty-state">
            <h3>No families yet</h3>
            <p>Create a new family or join an existing one to get started.</p>
          </div>
        ) : (
          <div className="families-grid">
            {families.map(family => (
              <FamilyCard 
                key={family.id}
                family={family}
                onLeave={handleLeaveFamily}
                onDelete={handleDeleteFamily}
                onFamilyUpdated={loadFamilies}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Family Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <CreateFamilyForm 
              onFamilyCreated={handleFamilyCreated}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      )}

      {/* Join Family Modal */}
      {showJoinForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <JoinFamilyForm 
              onFamilyJoined={handleFamilyJoined}
              onCancel={() => setShowJoinForm(false)}
            />
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default FamilyPage;
